import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { Meta } from '../../db/tipos'
import { ROTULOS_TIPO_META, ehMetaEmDinheiro } from '../../metas/rotulos'
import { lerCampoAlvo } from '../../metas/alvos'
import CampoMoeda from '../../componentes/CampoMoeda'

export interface MetaNova {
  escopo: string
  tipo: Meta['tipo']
  valorAlvo: number
}

const CAMPO = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm'

// Os campos de uma meta nova (escopo, tipo, valor), SEM as datas: cada tela
// preenche o periodo a partir do seu proprio mes.
//
// ⚠ Componente compartilhado por decisao dela (plano, passo 6b): o "repetir"
// precisa acrescentar meta sem sair do meio da tarefa -- no celular, ir para
// outra tela e voltar se perde. A porta "Criar" usa o MESMO formulario; se cada
// tela tiver o seu, a extracao nao serviu para nada.
export default function FormMetaNova({
  aoIncluir, rotuloBotao = 'Incluir', tipoInicial = 'valor_vendido',
}: {
  aoIncluir: (meta: MetaNova) => void
  rotuloBotao?: string
  // Os exemplos da tela de Metas abrem o formulario com o tipo ja escolhido.
  tipoInicial?: Meta['tipo']
}) {
  const [escopo, setEscopo] = useState('pessoal')
  const [tipo, setTipo] = useState<Meta['tipo']>(tipoInicial)
  const [alvo, setAlvo] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'), [])

  function incluir() {
    // ⚠ A leitura mora em alvos.ts, nao aqui: e a mesma regra de virgula
    // decimal do criador, e "1.234,50" com Number() vira NaN.
    const leitura = lerCampoAlvo(alvo)
    if (leitura.estado !== 'ok') {
      // Antes um valor ilegivel simplesmente nao fazia nada ao clicar, e ela
      // ficava sem saber se o botao estava quebrado.
      setAviso(
        leitura.estado === 'vazio'
          ? 'Preencha o valor alvo.'
          : 'Valor alvo inválido — use números, como 15.000 ou 1.234,50.',
      )
      return
    }
    setAviso(null)
    aoIncluir({ escopo, tipo, valorAlvo: leitura.valor })
    setAlvo('')
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <label className="block">
        <span className="block text-xs text-slate-500 mb-0.5">Escopo</span>
        <select
          data-testid="form-escopo" value={escopo}
          onChange={(e) => setEscopo(e.target.value)} className={CAMPO}
        >
          <option value="pessoal">Pessoal (minhas metas)</option>
          {representadas?.map((r) => (
            <option key={r.id} value={`representada:${r.id}`}>{r.nome}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="block text-xs text-slate-500 mb-0.5">Tipo</span>
        <select
          data-testid="form-tipo" value={tipo}
          onChange={(e) => setTipo(e.target.value as Meta['tipo'])} className={CAMPO}
        >
          {Object.entries(ROTULOS_TIPO_META).map(([valor, rotulo]) => (
            <option key={valor} value={valor}>{rotulo}</option>
          ))}
        </select>
      </label>
      <div className="flex gap-2">
        {/* Era um <input> pelado: sem inputMode, o celular abre o teclado de
            LETRAS para digitar dinheiro. E o aviso logo abaixo ja ensinava
            "15.000 ou 1.234,50" -- formato que, ate 16/08/2026, a propria
            conversao nao entendia ("15.000" virava 15). */}
        {/* Teste dela 08/10: so os tipos de DINHEIRO usam o campo com R$ --
            "Clientes positivados" com R$ na frente mentia. */}
        {ehMetaEmDinheiro(tipo) ? (
          <CampoMoeda
            data-testid="form-alvo" value={alvo}
            onChange={(texto) => { setAviso(null); setAlvo(texto) }}
            placeholder="Valor alvo" className={CAMPO}
          />
        ) : (
          <input
            data-testid="form-alvo" value={alvo} inputMode="decimal"
            onChange={(e) => { setAviso(null); setAlvo(e.target.value) }}
            placeholder={tipo === 'cobertura_linhas' ? 'Percentual alvo' : 'Quantidade alvo'}
            className={`${CAMPO} min-w-0 flex-1`}
          />
        )}
        <button
          data-testid="incluir-meta" type="button" onClick={incluir}
          className="bg-teal-700 text-white rounded-lg px-4 text-sm shrink-0"
        >
          {rotuloBotao}
        </button>
      </div>
      {aviso && <p data-testid="form-aviso" className="text-sm text-amber-700">{aviso}</p>}
    </div>
  )
}
